// Prescription data formatter utility
export const formatPrescriptionData = (consultationData) => {
  const generateComplaintNo = (index) => `C-${String(index + 1).padStart(3, '0')}`;
  const generateDiagnosisNo = (index) => `D-${String(index + 1).padStart(4, '0')}`;
  const generateMedicineNo = (index) => `M-${String(index + 1).padStart(3, '0')}`;
  const getCurrentDate = () => new Date().toISOString().split('T')[0];

  const formatComplaints = (complaints = []) => {
    return complaints.map((complaint, index) => ({
      complaint_no: generateComplaintNo(index),
      complaint: complaint.complaint || complaint.text || complaint,
      severity: complaint.severity || "Moderate",
      duration: complaint.duration || "1 week",
      date: complaint.date || getCurrentDate()
    }));
  };

  const formatDiagnosis = (diagnosis = []) => {
    return diagnosis.map((diag, index) => ({
      diagnosis_no: generateDiagnosisNo(index + 1001),
      diagnosis: diag.diagnosis || diag.text || diag,
      duration: diag.duration || "1 week",
      date: diag.date || getCurrentDate()
    }));
  };

  // Frequency mapping for converting patterns to readable text
  const frequencyMapping = {
    '0000': 'No dose',
    '000|': 'Night',
    '00|0': 'Evening', 
    '00||': 'Evening, Night',
    '0|00': 'Afternoon',
    '0|0|': 'Afternoon, Night',
    '0||0': 'Afternoon, Evening',
    '0|||': 'Afternoon, Evening, Night',
    '|000': 'Morning',
    '|00|': 'Morning, Night',
    '|0|0': 'Morning, Evening',
    '|0||': 'Morning, Evening, Night',
    '||00': 'Morning, Afternoon',
    '||0|': 'Morning, Afternoon, Night',
    '|||0': 'Morning, Afternoon, Evening',
    '||||': 'Morning, Afternoon, Evening, Night'
  };

  const parseFromToDate = (fromToString) => {
    if (!fromToString) return { from_date: getCurrentDate(), to_date: getCurrentDate() };
    
    const parts = fromToString.split(' to ');
    if (parts.length === 2) {
      return {
        from_date: parts[0].trim(),
        to_date: parts[1].trim()
      };
    }
    
    return { from_date: getCurrentDate(), to_date: getCurrentDate() };
  };

  const formatMedicines = (medicines = []) => {
    console.log('🔥 Formatting medicines:', medicines);
    console.log('🔥 Medicines count:', medicines.length);
    
    return medicines.map((medicine, index) => {
      console.log(`🔥 Processing medicine ${index + 1}:`, medicine);
      
      const dateRange = parseFromToDate(medicine.fromTo);
      const frequencyText = frequencyMapping[medicine.frequency] || medicine.frequency || "";
      
      const formattedMedicine = {
        medicine_no: generateMedicineNo(index + 1),
        name: medicine.name || "",
        type: medicine.type || "Tablet",
        dosage: medicine.dosage || "",
        frequency: frequencyText,
        duration: medicine.duration || "",
        when_to_take: medicine.howToTake || medicine.when_to_take || "After meals",
        from_date: dateRange.from_date,
        to_date: dateRange.to_date
      };
      
      console.log(`🔥 Formatted medicine ${index + 1}:`, formattedMedicine);
      return formattedMedicine;
    });
  };

  const formatAdvice = (advice = []) => {
    return advice.map((adviceItem) => {
      const formatted = {
        advice_text: adviceItem.advice || adviceItem.text || adviceItem,
        date: adviceItem.date || getCurrentDate()
      };
      
      // Preserve translations if they exist
      if (adviceItem.translations) {
        formatted.translations = adviceItem.translations;
      }
      
      // Preserve id if it exists (useful for tracking)
      if (adviceItem.id) {
        formatted.id = adviceItem.id;
      }
      
      return formatted;
    });
  };

  const formatClinicalFindings = (clinicalFindings = []) => {
    console.log('🔥 Formatting clinical findings:', clinicalFindings);
    console.log('🔥 Clinical findings count:', clinicalFindings.length);
    
    const formatted = clinicalFindings.map((findingItem) => {
      console.log('🔥 Processing finding:', findingItem);
      const formatted = {
        finding_no: findingItem.finding_no || '',
        finding: findingItem.finding || findingItem.text || findingItem
      };
      console.log('🔥 Formatted finding:', formatted);
      return formatted;
    });
    
    console.log('🔥 Final formatted clinical findings:', formatted);
    return formatted;
  };

  const formatPasthistorys = (PasthistoryData = []) => {
    console.log('🔥 Formatting Past history entries:', PasthistoryData);
    const getCurrentDateSafe = getCurrentDate;
    return PasthistoryData.map((item, index) => {
      // Accept both new and legacy shapes.
      const pasthistoryText = item.pasthistory || item.history || item.text || '';
      // If legacy numbering (history_no like F1 / F-001) normalize to H-###
      let pasthistory_no = item.pasthistory_no || (item.history_no && /^H\d{3}$/i.test(item.history_no) ? item.history_no.replace(/^H(\d{3})$/i,'H-$1') : '') || item.history_no || '';
      if (!pasthistory_no) {
        pasthistory_no = `H-${String(index + 1).padStart(3,'0')}`;
      } else if (/^F/i.test(pasthistory_no)) {
        // Replace leading F or F- with H-
        pasthistory_no = pasthistory_no.replace(/^F-?/i, 'H-');
      }
      // Derive plain history_no variant (H###) for backward compatibility
      let history_no = item.history_no || pasthistory_no.replace(/^H-(\d{3})$/i,'H$1');
      const formatted = {
        pasthistory_no,
        history_no,
        pasthistory: pasthistoryText,
        history: pasthistoryText,
        duration: item.duration || '1 week',
        date: item.date || getCurrentDateSafe()
      };
      console.log('🔥 Formatted past history:', formatted);
      return formatted;
    });
  };

  const formatAllergies = (allergiesData = {}) => {
    const allergies = [];
    
    // Get the arrays for each allergy type, defaulting to empty arrays
    const medicines = Array.isArray(allergiesData.medicines) ? allergiesData.medicines : [];
    const food = Array.isArray(allergiesData.food) ? allergiesData.food : [];
    const others = Array.isArray(allergiesData.other || allergiesData.others) ? 
      (allergiesData.other || allergiesData.others) : [];
    
    // Find the maximum length among all allergy arrays
    const maxLength = Math.max(medicines.length, food.length, others.length, 1);
    
    // Create an array of allergy objects
    for (let i = 0; i < maxLength; i++) {
      allergies.push({
        medicines: medicines[i] || "",
        food: food[i] || "",
        others: others[i] || ""
      });
    }
    
    return allergies;
  };

  const formatReferredTo = (consultationData) => {
    if (consultationData.referredDoctor) {
      return [
        {
          referred_doctor_name: consultationData.referredDoctor || "",
          speciality: consultationData.referredSpeciality || "",
          doctor_phone: consultationData.referredPhone || "",
          doctor_email: consultationData.referredEmail || "",
          hospital: consultationData.referredHospital || "City Hospital",
          hospital_address: consultationData.referredHospitalAddress || "123 Main St",
          phone: consultationData.referredPhone || "",
          email: consultationData.referredEmail || "",
          date: new Date().toISOString().split('T')[0]
        }
      ];
    }
    return null;
  };

  // Format parameters if they exist
  const formatParameters = (parameters = {}) => {
    console.log('🔥 Formatting parameters:', parameters);
    // If parameters is already an array, return it as is
    if (Array.isArray(parameters)) {
      return parameters;
    }
    // If parameters is an object, convert it to an array of { key, value } pairs
    if (parameters && typeof parameters === 'object') {
      return Object.entries(parameters).map(([key, value]) => ({
        name: key,
        value: value
      }));
    }
    return [];
  };

  const formattedData = {
    appointment_id: parseInt(consultationData.appointment_id) || 0,
    next_visit:
      consultationData.followUpDate ||
      consultationData.nextVisitDate ||
      getCurrentDate(),
    investigation: Array.isArray(consultationData.investigationsList)
      ? consultationData.investigationsList.join(", ")
      : consultationData.investigation || "",
    past_medication: consultationData.pastMedication || "",
    complaints: formatComplaints(consultationData.complaints),
    clinical_findings: formatClinicalFindings(
      consultationData.clinicalFindingsData ||
        consultationData.clinical_findings
    ),
    past_history: formatPasthistorys(consultationData.PastHistoryData),
    diagnosis: formatDiagnosis(consultationData.diagnosis),
    medicines: formatMedicines(
      consultationData.medicinesData || consultationData.medicines
    ),
    advice: formatAdvice(
      consultationData.advicesData || consultationData.advice
    ),
    allergies: formatAllergies(consultationData.allergies),
    parameters: formatParameters(consultationData.parameters),
  };

  const referredTo = formatReferredTo(consultationData);
  if (referredTo) {
    formattedData.referred_to = referredTo;
  }

  return formattedData;
};

export const validatePrescriptionData = (prescriptionData) => {
  const errors = [];

  if (!prescriptionData.appointment_id || prescriptionData.appointment_id === 0) {
    errors.push("Appointment ID is required");
  }

  if (!prescriptionData.next_visit) {
    errors.push("Next visit date is required");
  }

  if (!Array.isArray(prescriptionData.complaints) || prescriptionData.complaints.length === 0) {
    errors.push("At least one complaint is required");
  }

  if (!Array.isArray(prescriptionData.diagnosis) || prescriptionData.diagnosis.length === 0) {
    errors.push("At least one diagnosis is required");
  }

  if (!Array.isArray(prescriptionData.medicines) || prescriptionData.medicines.length === 0) {
    errors.push("At least one medicine is required");
  }

  if (!Array.isArray(prescriptionData.advice) || prescriptionData.advice.length === 0) {
    errors.push("At least one advice is required");
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};